import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import axios from 'axios';
import { NIL } from 'uuid';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';
// heartbeat cadence; the server expires a lock after ~2 min with no heartbeat
const HEARTBEAT_MS = 30000;

export type EditLockStatus = 'idle' | 'mine' | 'locked';

export interface EditLock {
  status: EditLockStatus;
  // when locked by someone else, who holds it
  holderName: string | null;
  // someone else holds the lock → this editor is read-only until they Take Over
  readOnly: boolean;
  // forcibly grab the lock (the Take Over button)
  takeOver: () => Promise<void>;
}

// item 213 — pessimistic presence lock for a single record (a project or a document).
// Acquires the lock when the edit form mounts, heartbeats every ~30s to keep it, and
// releases on unmount (save / cancel / navigate-away). If another user holds it (or takes
// it over mid-edit), `readOnly` flips true and `holderName` names them so the form can
// show the locked banner + Take Over. Disabled (no-op) for a brand-new, unsaved record.
const useEditLock = (entityType: string, entityId: string | undefined): EditLock => {
  const enabled = !!entityType && !!entityId && entityId !== NIL;
  const [status, setStatus] = useState<EditLockStatus>('idle');
  const [holderName, setHolderName] = useState<string | null>(null);

  const apply = (data: { held?: boolean; holderName?: string | null } | undefined) => {
    if (data?.held) {
      setStatus('mine');
      setHolderName(null);
    } else {
      setStatus('locked');
      setHolderName(data?.holderName ?? null);
    }
  };

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const body = { entityType, entityId };

    const acquire = async () => {
      try {
        const { data } = await axios.post(`${apiUrl()}EditLock/acquire`, body);
        if (!cancelled) apply(data);
      } catch { /* best-effort — locking never blocks editing on a network blip */ }
    };
    const beat = async () => {
      try {
        const { data } = await axios.post(`${apiUrl()}EditLock/heartbeat`, body);
        if (!cancelled) apply(data);
      } catch { /* best-effort */ }
    };

    acquire();
    const iv = window.setInterval(beat, HEARTBEAT_MS);

    return () => {
      cancelled = true;
      window.clearInterval(iv);
      // release on unmount (navigate away / close the form). Best-effort; if it doesn't
      // land (hard tab close), the server's heartbeat timeout reclaims the lock.
      axios.post(`${apiUrl()}EditLock/release`, body).catch(() => { /* ignore */ });
    };
  }, [enabled, entityType, entityId]);

  const takeOver = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await axios.post(`${apiUrl()}EditLock/takeover`, { entityType, entityId });
      apply(data);
    } catch { /* ignore — they can retry */ }
  }, [enabled, entityType, entityId]);

  return {
    status, holderName, readOnly: status === 'locked', takeOver,
  };
};

export default useEditLock;
